import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {fetchMinBrowserConfig} from "../../services/configService";
import { logAudit } from '../../utils/logAudit';
import {CardGrid} from "./CardGrid";

function detectBrowser() {
    const ua = navigator.userAgent;

    // Orden importa para Edge/Chrome/Opera
    if (/Edg\/(\d+\.\d+)/.test(ua)) {
        return { name: 'edge', version: RegExp.$1, displayName: 'Microsoft Edge' };
    }
    if (/OPR\/(\d+\.\d+)/.test(ua) || /Opera\/(\d+\.\d+)/.test(ua)) {
        return { name: 'opera', version: RegExp.$1, displayName: 'Opera' };
    }
    if (/Chrome\/(\d+\.\d+)/.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)) {
        return { name: 'chrome', version: RegExp.$1, displayName: 'Google Chrome' };
    }
    // Chrome en iOS usa CriOS
    if (/CriOS\/(\d+\.\d+)/.test(ua)) {
        return { name: 'chrome', version: RegExp.$1, displayName: 'Google Chrome (iOS)' };
    }
    if (/Firefox\/(\d+\.\d+)/.test(ua)) {
        return { name: 'firefox', version: RegExp.$1, displayName: 'Mozilla Firefox' };
    }
    // Safari: usa Version/x.y ; evita Safari dentro de Chrome iOS
    if (/Version\/(\d+\.\d+).*Safari/.test(ua) && !/Chrome|CriOS|OPR|Edg\//.test(ua)) {
        return { name: 'safari', version: RegExp.$1, displayName: 'Safari' };
    }
    return { name: 'unknown', version: '0.0', displayName: 'Navegador' };
}

function cmpVersions(a, b) {
    // compara "128.0.2" vs "128.0"
    const pa = String(a).split('.').map(Number);
    const pb = String(b).split('.').map(Number);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
        const x = pa[i] || 0;
        const y = pb[i] || 0;
        if (x < y) return -1;
        if (x > y) return 1;
    }
    return 0;
}



export const CardActions = ({ canSign }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const normalizeVersion = (v) => {
        if (!v) return null;
        const m = String(v).match(/(\d+(?:\.\d+)*|\d+)/);
        return m ? m[1] : null;
    };


    const handleClick = async () => {
        if (!canSign) return;

        // Tu detección local actual (la seguimos usando para comparar):
        const { version, displayName } = detectBrowser();

        try {
            const dto = await fetchMinBrowserConfig(); // { type, version, label } | null
            const userAgent = navigator.userAgent;
            if (!dto) {
                try {
                    logAudit({
                        state: 'INFO',
                        event: 'BROWSER_COMPAT_CHECK',
                        message: `Browser check: UA sent, no backend policy returned → ENABLE viewer`,
                        args: {
                            userAgent,
                            detectedDisplay: displayName,
                            detectedVersion: version,
                            backendPolicy: null,
                            decision: 'ENABLE'
                        }
                    });
                } catch {}
                // Sin config → por seguridad NO bloqueamos (o cambia a bloquear si lo preferís)
                sessionStorage.removeItem('browserBlocked');
                sessionStorage.removeItem('browserBlockMessage');
                navigate('signDocument');
                return;
            }

            const minRaw = dto.version;            // p.ej. "121", "ESR 128", "17.4"
            const label  = dto.label || `${displayName} ${minRaw}`;

            const currentNorm = normalizeVersion(version);
            const minNorm     = normalizeVersion(minRaw);

            const shouldBlock = !!minNorm && !!currentNorm && cmpVersions(currentNorm, minNorm) < 0;
            try {
                logAudit({
                    state: 'INFO',
                    event: 'BROWSER_COMPAT_CHECK',
                    message: `Browser check: UA sent "${displayName} ${version}", backend requires "${dto.type || 'n/a'} ${dto.version}" (${label}) → ${shouldBlock ? 'DISABLE viewer' : 'ENABLE viewer'}`,
                    args: {
                        userAgent,
                        detectedDisplay: displayName,
                        detectedVersion: version,
                        backendPolicy: { type: dto.type || null, version: dto.version || null, label },
                        normalized: { current: currentNorm, min: minNorm },
                        decision: shouldBlock ? 'DISABLE' : 'ENABLE'
                    }
                });
            } catch {}

            if (minNorm && currentNorm && cmpVersions(currentNorm, minNorm) < 0) {
                // Bloqueamos en la vista del visor
                sessionStorage.setItem('browserBlocked', '1');
                sessionStorage.setItem(
                    'browserBlockMessage',
                    `No es posible mostrar el documento. Se requiere la siguiente versión mínima del navegador web: ${label}`
                );
            } else {
                sessionStorage.removeItem('browserBlocked');
                sessionStorage.removeItem('browserBlockMessage');
            }
        } catch {
            // Si falla la config, NO bloqueamos (ajústalo si querés lo contrario)
            sessionStorage.removeItem('browserBlocked');
            sessionStorage.removeItem('browserBlockMessage');
        }
        sessionStorage.setItem('viewerEntry', 'fromUpload');
        navigate('signDocument');
    };


    return (
        <div className="card-actions">
            <button
                type="button"
                className={`btn-medium btn-primary ${!canSign ? 'disable-link' : ''}`}
                disabled={!canSign}
                onClick={handleClick}
            >
                {t('cardActions.signButton')}
            </button>
        </div>
    );
};
